import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { listReturnUrl } from '../lib/listReturn';
import { ROLE, useAuth } from '../lib/auth';
import { clientQtyLine, splitSheets } from '../lib/jobCardQty';
import html2pdf from 'html2pdf.js';
import { sheetDimsForCard } from '../lib/units';

interface PrintRow {
  Job_ID: string;
  PO_RefNumber: string | null;
  Job_Name: string | null;
  JobOrder_date: string | null;
  Total_Boxes: string | null;
  TotalSheet_withWastage: string | null;
  Ups: string | null;
  Die_Cutting: string | null;
  Selected_Die: string | null;
  Sheet_Size: string | null;
  Sheet_CutSize_length: string | null;
  Sheet_CutSize_Width: string | null;
  Paper_Type: string | null;
  Lamination_Type: string | null;
  Machine: string | null;
  Remarks: string | null;
  PO_Number: string | null;
  PO_Date: string | null;
  PO_deliverydate: string | null;
  PO_Quantity: string | null;
  PO_Artwork: string | null;
  PO_OrderType: string | null;
  Job_OrderType: string | null;
  Client_Name: string | null;
  Client_Address_Billing: string | null;
  Client_Address_Dispatch: string | null;
  Client_Email: string | null;
  Client_Phone: string | null;
  Product_Name: string | null;
  Product_Length: string | null;
  Product_Width: string | null;
  Product_Height: string | null;
  Product_Ups: string | null;
  Product_Artwork: string | null;
  Product_AW_Material_No: string | null;
  FoilingColor: string | null;
  FilmSize_Micron: string | null;
  EmbossingNo: string | null;
  ShadeCardNo: string | null;
  PrePostLamination: string | null;
  OnlineEmbossing: boolean | null;
  OfflineEmbossing: boolean | null;
  Braille: boolean | null;
  SpotUV: boolean | null;
  ScreenUV: boolean | null;
  OfflineCoating: boolean | null;
  OnlineCoating: boolean | null;
  BaseCoat: boolean | null;
  CartonType: string | null;
  MetallicCarton: boolean | null;
  ThreePlyCarton: boolean | null;
  CartonSize_L: string | null;
  CartonSize_W: string | null;
  CartonSize_H: string | null;
  MachineFront: string | null;
  MachineFront_Color: string | null;
  MachineFront2: string | null;
  MachineFront2_Color: string | null;
  MachineFront2_Name?: string | null;
  MachineBack: string | null;
  MachineBack_Color: string | null;
  MachineFront_Name: string | null;
  MachineBack_Name: string | null;
  Die_Color: string | null;
  PaperType_Name: string | null;
  Sheet_GSM_From_Sheet: string | null;
  Product_CutSize_L: string | null;
  Product_CutSize_W: string | null;
  Product_Remark: string | null;
  PlateNumber: string | null;
  Lamination_Type_Name: string | null;
  Remark_Party_GSM: string | null;
  BP_No: string | null;
  Product_ID: string | null;
  Resolved_Sheet_Size: string | null;
  ReissueReason: string | null;
  // Change-Spec + New Job Card flow.  When present, this card replaced
  // an earlier one with different specs; the callout at the top of the
  // print tells the floor why they are running a fresh setup.
  SpecChangeReason: string | null;
  RespawnedFromJobId: string | null;
  Client_Qty: string | null;
  CutToCutSize: string | null;
  // PO qty the card recorded when this version was saved; null on cards
  // saved before it was kept.
  JobPOQty?: number | string | null;
  // When this version was issued, if the card has been re-saved since it was
  // first made; otherwise the print uses JobOrder_date.
  Card_Issued_On?: string | null;
}

interface LinkedPo {
  PO_ID: string;
  PO_Number: string | null;
  PO_Date: string | null;
  PO_Quantity: string | null;
  /** The PO's job name — what a clubbed card calls each product. */
  PO_Job_Name?: string | null;
  // The PO's product: a card clubbing two products prints each one.
  Product_ID?: string | null;
  Product_Name?: string | null;
  Product_Length?: string | null;
  Product_Width?: string | null;
  Product_Height?: string | null;
  CutToCutSize?: string | null;
  Product_Artwork?: string | null;
  Product_AW_Material_No?: string | null;
  PlateNumber?: string | null;
  ShadeCardNo?: string | null;
  BP_No?: string | null;
  Product_Remark?: string | null;
}

/** One product on the card, with the POs it came on and what identifies its design. */
interface CardProduct {
  name: string;
  poNumbers: string[];
  qty: number;
  /** L × W × H — different products are different sizes. */
  size: string;
  kToK: string;
  design: string;
  artMat: string;
  plate: string;
  shade: string;
  bp: string;
  remark: string;
}

/**
 * The products a card carries, the card's own first, each called by its
 * PO's job name (else its product's name).  The card's own product reads
 * from the card (frozen at creation); a clubbed one from its Product Master.
 * A PO with no product picked (older POs) is the card's own product when it
 * is the card's main PO or carries the same job name; otherwise it is listed
 * by its job name, with nothing known of its size.
 */
function cardProducts(r: PrintRow, linkedPos: LinkedPo[]): CardProduct[] {
  const norm = (v: string | null | undefined) => (v ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
  const size = (l?: string | null, w?: string | null, h?: string | null) => [l, w, h].filter(Boolean).join(' × ');
  type Entry = CardProduct & { jobName: string; productName: string; pos: number };
  const entry = (productName: string, v: Omit<CardProduct, 'name' | 'poNumbers' | 'qty'>): Entry =>
    ({ ...v, name: '', poNumbers: [], qty: 0, jobName: '', productName, pos: 0 });

  const mainKey = norm(r.Product_ID) || '#MAIN';
  const out = new Map<string, Entry>([[mainKey, entry(r.Product_Name ?? '', {
    size: size(r.Product_Length, r.Product_Width, r.Product_Height), kToK: r.CutToCutSize ?? '',
    design: r.Product_Artwork ?? r.PO_Artwork ?? '', artMat: r.Product_AW_Material_No ?? '',
    plate: r.PlateNumber ?? '', shade: r.ShadeCardNo ?? '', bp: r.BP_No ?? '', remark: r.Product_Remark ?? '',
  })]]);
  const mainJob = norm(linkedPos.find((p) => p.PO_ID === r.PO_RefNumber)?.PO_Job_Name) || norm(r.Job_Name);

  for (const p of linkedPos) {
    const job = (p.PO_Job_Name ?? '').trim();
    const key = norm(p.Product_ID)
      || (p.PO_ID === r.PO_RefNumber || !job || norm(job) === mainJob ? mainKey : `#JOB:${norm(job)}`);
    if (!out.has(key)) {
      out.set(key, entry(p.Product_Name ?? p.Product_ID ?? '', {
        size: size(p.Product_Length, p.Product_Width, p.Product_Height), kToK: p.CutToCutSize ?? '',
        design: p.Product_Artwork ?? '', artMat: p.Product_AW_Material_No ?? '',
        plate: p.PlateNumber ?? '', shade: p.ShadeCardNo ?? '', bp: p.BP_No ?? '', remark: p.Product_Remark ?? '',
      }));
    }
    const e = out.get(key)!;
    if (!e.jobName && job) e.jobName = job;
    e.pos += 1;
    if (p.PO_Number && !e.poNumbers.includes(p.PO_Number)) e.poNumbers.push(p.PO_Number);
    e.qty += Number(p.PO_Quantity) || 0;
  }
  // The card's own product drops out when none of its POs is for it.
  return [...out.values()]
    .filter((e) => e.pos > 0 || linkedPos.length === 0)
    .map(({ jobName, productName, pos: _pos, ...e }) => ({ ...e, name: jobName || productName || r.Job_Name || '' }));
}

type PrintResp = PrintRow & { linkedPos?: LinkedPo[] };

/**
 * The PO qty this version of the card was issued against: what the card
 * recorded when it was saved, else (cards saved before that was kept) the
 * PO's own figure, summed over linked POs.  Empty when that is unknown.
 */
function cardPoQty(r: PrintRow, linkedPos: LinkedPo[]): string {
  if (r.JobPOQty != null && r.JobPOQty !== '') return String(r.JobPOQty);
  if (linkedPos.length > 1) return String(linkedPos.reduce((s, p) => s + (Number(p.PO_Quantity) || 0), 0));
  return r.PO_Quantity ?? '';
}

function dt(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function yn(b: boolean | null): string { return b ? 'Yes' : 'No'; }
function esc(v: string | null | undefined): string {
  if (!v) return '';
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function JobCardPrintPage() {
  const { id } = useParams();
  const decoded = id ? decodeURIComponent(id) : '';
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['job-print', decoded],
    queryFn: () => api<PrintResp>(`/api/job-orders/${encodeURIComponent(decoded)}/print`),
  });
  // Super-Admin-only actions: refresh re-freezes at CURRENT master values;
  // clear removes the freeze so the card starts tracking master edits.
  // Every JC is otherwise frozen at creation.
  const refreshSnap = useMutation({
    mutationFn: () => api<{ ok: true; refreshed: boolean; changed?: string[]; differs?: string[] }>(
      `/api/job-orders/${encodeURIComponent(decoded)}/refresh-snapshot`,
      { method: 'POST' },
    ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['job-print', decoded] });
      // Say what the pull changed on the card, and what still differs from the master.
      const lines = [
        r.changed?.length ? `Updated from the master:\n${r.changed.join('\n')}` : 'Cut size and die already match the master.',
        r.differs?.length ? `\nStill different from the master (not changed here - reissue / edit the card for these):\n${r.differs.join('\n')}` : '',
      ];
      alert(lines.join('\n'));
    },
  });
  const clearSnap = useMutation({
    mutationFn: () => api<{ ok: true }>(
      `/api/job-orders/${encodeURIComponent(decoded)}/snapshot`,
      { method: 'DELETE' },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-print', decoded] }),
  });

  async function downloadPdf() {
    const el = printRef.current;
    if (!el || downloading) return;
    setDownloading(true);
    try {
      const filename = `Job-Card-${decoded.replace(/\//g, '-')}.pdf`;
      await html2pdf().set({
        margin: 0,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      } as any).from(el).save();
    } finally {
      setDownloading(false);
    }
  }

  if (q.isLoading) return <div className="p-6 text-slate-500">Loading job card...</div>;
  if (q.isError || !q.data) {
    return (
      <div className="p-6">
        <p className="text-red-600">Could not load job card: {(q.error as Error)?.message ?? 'unknown'}</p>
        <Link to={listReturnUrl('/job-orders')} className="text-brand underline text-sm">← Back to Job Orders</Link>
      </div>
    );
  }
  const r = q.data;
  const sheetSizeRaw = r.Resolved_Sheet_Size ?? r.Sheet_Size ?? '';
  const sheetGsm = r.Sheet_GSM_From_Sheet
    || (/\((\d+(?:\.\d+)?)\)\s*$/.exec(sheetSizeRaw)?.[1] ?? '')
    || r.Remark_Party_GSM
    || '';
  const orderType = (r.Job_OrderType ?? r.PO_OrderType ?? '').trim();
  // The mm figures print whole; the inches are worked out from the exact mm
  // (488.95 mm is 19.25 in — from the shortened 488 it came out as 19.21).
  const sheetSizeExact = sheetSizeRaw.replace(/\s*\(\d+(?:\.\d+)?\)\s*$/, '');
  const sheetCutExact = [r.Sheet_CutSize_length, r.Sheet_CutSize_Width].filter(Boolean).join(' x ');
  // Whole mm with the inches beside; a legacy card's inches as they were entered.
  const { display: sheetSizeDisplay, suffix: sheetSizeIn } = sheetDimsForCard(sheetSizeExact);
  const { display: sheetCutDisplay, suffix: sheetCutIn } = sheetDimsForCard(sheetCutExact);

  const lp = r.linkedPos ?? [];
  const poQty = cardPoQty(r, lp);
  const sheets = splitSheets(r.Client_Qty, r.Ups, r.TotalSheet_withWastage, r.Total_Boxes, poQty);

  const cardHtml = buildCardHtml(r, { sheetSizeDisplay, sheetCutDisplay, sheetSizeIn, sheetCutIn, sheetGsm, orderType, sheets, linkedPos: lp, poQty });

  return (
    <>
      {/* Print-specific styles: hide toolbar, no margins */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          .no-print { display: none !important; }
          body { margin: 0 !important; padding: 0 !important; }
          @page { margin: 0; size: A4; }
        }
      `}} />

      {/* Toolbar — hidden in print via class */}
      <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Arial, sans-serif' }}>
        <Link to={listReturnUrl('/job-orders')} style={{ fontSize: '13px', color: '#475569', textDecoration: 'none' }}>← Back to Job Orders</Link>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isSuperAdmin && (
            <>
              <button
                onClick={() => {
                  if (window.confirm('Pull the CURRENT product-master values into this card?\n\nThe card re-freezes at what the master says right now.  Future master edits will NOT change it.')) {
                    refreshSnap.mutate();
                  }
                }}
                disabled={refreshSnap.isPending || clearSnap.isPending}
                title="Pull master → re-freeze this JC at current master values (Super Admin only)"
                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a', fontSize: '13px', fontWeight: 600, cursor: refreshSnap.isPending ? 'not-allowed' : 'pointer', opacity: refreshSnap.isPending ? 0.7 : 1 }}>
                {refreshSnap.isPending ? 'Refreshing…' : 'Pull Master'}
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Clear this card\'s frozen snapshot?\n\nThe card will start reading LIVE from the product master — any future edit to that product will appear here.  Use "Pull Master" to re-freeze later.')) {
                    clearSnap.mutate();
                  }
                }}
                disabled={refreshSnap.isPending || clearSnap.isPending}
                title="Clear snapshot → this JC tracks master edits going forward (Super Admin only)"
                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', fontSize: '13px', fontWeight: 600, cursor: clearSnap.isPending ? 'not-allowed' : 'pointer', opacity: clearSnap.isPending ? 0.7 : 1 }}>
                {clearSnap.isPending ? 'Clearing…' : 'Clear Snapshot'}
              </button>
              <span style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0' }} />
            </>
          )}
          <button onClick={() => window.print()}
            style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Print
          </button>
          <button onClick={downloadPdf} disabled={downloading}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#014868', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.7 : 1 }}>
            {downloading ? 'Generating…' : 'Save PDF'}
          </button>
        </div>
      </div>

      {/* Card — this is what gets printed & PDF'd */}
      <div
        ref={printRef}
        style={{ width: '210mm', margin: '0 auto', padding: '12mm 15mm', boxSizing: 'border-box', background: '#fff', color: '#000', lineHeight: '1.4', fontFamily: 'Arial, Helvetica, sans-serif' }}
        dangerouslySetInnerHTML={{ __html: cardHtml }}
      />
    </>
  );
}

function buildCardHtml(
  r: PrintRow,
  d: {
    sheetSizeDisplay: string; sheetCutDisplay: string;
    /** " (19.25 x 19.33 in)" — from the exact mm, not the whole-mm figures shown. */
    sheetSizeIn: string; sheetCutIn: string;
    sheetGsm: string; orderType: string;
    sheets: { base: string; wastage: string }; linkedPos: LinkedPo[]; poQty: string;
  },
): string {
  const { sheetSizeDisplay, sheetCutDisplay, sheetSizeIn, sheetCutIn, sheetGsm, orderType, sheets, linkedPos, poQty } = d;
  const crq = clientQtyLine(r.Client_Qty, poQty);
  // Two products on one card (clubbed POs whose specs match): both names,
  // and a table of what identifies each design.
  const products = cardProducts(r, linkedPos);
  const multi = products.length > 1;
  /**
   * A value each product has its own of (size, design no., …): on a card
   * with two products, both — each on its line with the product's name —
   * or once when they are the same.
   */
  const each = (get: (p: CardProduct) => string, single: string | null | undefined): string => {
    if (!multi) return esc(single);
    const vals = products.map((p) => (get(p) ?? '').trim());
    const key = (v: string) => v.toUpperCase().replace(/\s+/g, '');
    if (vals.every((v) => key(v) === key(vals[0]!))) return esc(vals[0]);
    // A value never breaks inside (219.50 must not print as 2 / 19.50); the
    // line may wrap between the name and the value.
    return products.map((p, i) =>
      `<div style="word-break:normal;overflow-wrap:break-word;"><span style="font-weight:800;">${esc(p.name)}:</span> <span style="white-space:nowrap;">${esc(vals[i]) || '—'}</span></div>`,
    ).join('');
  };
  const poNumbers = [...new Set(linkedPos.map((p) => p.PO_Number ?? '').filter(Boolean))];
  const poDates = [...new Set(linkedPos.map((p) => dt(p.PO_Date)).filter(Boolean))];

  // No cell borders — only the section heading rules separate the card.
  const thS = 'padding:2px 10px 2px 0;font-weight:800;font-size:11px;vertical-align:top;color:#000;';
  const tdS = 'padding:2px 10px 2px 0;font-weight:500;font-size:11.5px;vertical-align:top;color:#1a1a1a;word-break:break-all;overflow-wrap:break-word;';
  const tblS = 'width:100%;table-layout:fixed;border-collapse:collapse;margin-bottom:5px;';
  const colG = '<colgroup><col style="width:17%"><col style="width:33%"><col style="width:17%"><col style="width:33%"></colgroup>';
  const shS = (t: string) => `<div style="font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:0.05em;padding:2px 0;margin-top:6px;border-bottom:2px solid #000;color:#000;">${t}</div>`;

  const th = (t: string) => `<td style="${thS}">${t}</td>`;
  const td = (t: string, span = 1) => `<td style="${tdS}"${span > 1 ? ` colspan="${span}"` : ''}>${t}</td>`;
  const row = (cells: string[]) => `<tr>${cells.join('')}</tr>`;

  const productRemarks = multi
    ? products.filter((p) => p.remark).map((p) => ({ label: `Product (${p.name})`, text: p.remark }))
    : r.Product_Remark ? [{ label: 'Product', text: r.Product_Remark }] : [];
  // pre-wrap on each remark, not on the block: the block's own line breaks
  // would otherwise print as blank lines.
  const remarkLine = (label: string, text: string, last: boolean) =>
    `<div style="white-space:pre-wrap;margin-bottom:${last ? '0' : '4px'}"><span style="font-weight:800;text-transform:uppercase;font-size:9px;color:#000;">${esc(label)}: </span>${esc(text)}</div>`;
  const remarkLines = [...productRemarks, ...(r.Remarks ? [{ label: 'Job', text: r.Remarks }] : [])];
  const remarksHtml = remarkLines.length ? `
    ${shS('Remarks')}
    <div style="padding:4px 0;margin-bottom:5px;font-size:10px;font-weight:500;color:#1a1a1a;">${
      remarkLines.map((l, i) => remarkLine(l.label, l.text, i === remarkLines.length - 1)).join('')
    }</div>` : '';

  const cellS = 'padding:2px 6px 2px 0;font-size:11px;vertical-align:top;border-bottom:1px solid #ddd;word-break:break-all;';
  const productsHtml = multi ? `
    ${shS(`Products on this card (${products.length})`)}
    <table style="width:100%;border-collapse:collapse;margin-bottom:5px;">
      <thead><tr>
        ${['Product', 'PO No.', 'PO Qty']
          .map((h) => `<td style="${cellS}font-weight:800;border-bottom:1px solid #000;">${h}</td>`).join('')}
      </tr></thead>
      <tbody>
        ${products.map((p) => `<tr>
          <td style="${cellS}font-weight:800;">${esc(p.name)}</td>
          <td style="${cellS}">${esc(p.poNumbers.join(', ')) || '—'}</td>
          <td style="${cellS}">${p.qty ? p.qty.toLocaleString('en-IN') : '—'}</td>
        </tr>`).join('')}
      </tbody>
    </table>
    <div style="font-size:9px;font-weight:600;color:#333;margin-bottom:4px;">Same Product Master setup (board, sheet, die, ups, machines, finish) for all ${products.length} products; where they differ (size, design …) the card prints each one's.</div>` : '';

  return `
    <div style="text-align:right;font-size:8px;font-weight:800;margin-bottom:2px;">PD/JC/01</div>

    <div style="border:2px solid #000;padding:6px 10px;margin-bottom:6px;text-align:center;">
      <div style="font-size:16px;font-weight:900;letter-spacing:0.05em;">VASUDEV PRINT PACK PVT. LTD.</div>
      <div style="font-size:8.5px;font-weight:600;">Hansa Industrial Park, Derabassi-Barwala Rd, Dera Bassi, Punjab 140507 · Phone: 085807 03745 · mis@vasudevprintpack.com</div>
      <div style="margin-top:2px;font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:0.15em;">Job Card</div>
    </div>

    ${r.SpecChangeReason || r.RespawnedFromJobId ? `
    <div style="margin-bottom:6px;padding:6px 10px;border:2px solid #1d4ed8;background:#eff6ff;">
      <span style="font-weight:900;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:#1d4ed8;">Change Spec${r.RespawnedFromJobId ? ` (supersedes ${esc(r.RespawnedFromJobId)})` : ''}: </span>
      <span style="font-weight:700;font-size:11.5px;color:#1a1a1a;">${esc(r.SpecChangeReason ?? '')}</span>
    </div>` : ''}

    <table style="${tblS}">${colG}<tbody>
      ${row([th('Job ID'), td(`${esc(r.Job_ID)}${orderType ? ` (${orderType.toUpperCase()})` : ''}`), th('PO Date'), td(linkedPos.length > 1 ? poDates.join(', ') : dt(r.PO_Date))])}
      ${row([th('Job Card Date'), td(dt(r.Card_Issued_On ?? r.JobOrder_date)), th('PO Qty'), td(esc(poQty) || '—')])}
      ${row([th(''), td(''), th('K to K Size'), td(each((p) => p.kToK, r.CutToCutSize))])}
      ${row([th(multi ? 'Product Names' : 'Product Name'), td(multi
        ? products.map((p) => esc(p.name)).join(' <b>+</b> ')
        : esc(r.Job_Name && r.Product_Name && r.Job_Name !== r.Product_Name ? r.Job_Name : (r.Product_Name ?? r.Job_Name)), 3)])}
      ${row([th('Client'), td(esc(r.Client_Name), 3)])}
      ${crq != null ? row([th(''), td(''), th(crq.label), td(esc(crq.value))]) : ''}
      ${row([th(poNumbers.length > 1 ? 'PO Nos.' : 'PO No.'), td(linkedPos.length > 1 ? poNumbers.map((n) => esc(n)).join(', ') : esc(r.PO_Number)), th('Total Print Box'), td(esc(r.Total_Boxes))])}
    </tbody></table>

    ${productsHtml}

    ${shS('Cutting')}
    <table style="${tblS}">${colG}<tbody>
      ${row([th('Board (Paper Type)'), td(esc(r.PaperType_Name ?? r.Paper_Type)), th('GSM'), td(esc(sheetGsm))])}
      ${row([th('Full Sheet Size'), td(`${esc(sheetSizeDisplay)}${sheetSizeIn}`), th('Final Cut Size'), td(`${esc(sheetCutDisplay)}${sheetCutIn}`)])}
      ${row([th('Actual Sheet'), td(sheets.base), th('Sheet (incl. W)'), td(esc(r.TotalSheet_withWastage))])}
      ${row([th('Wastage'), td(sheets.wastage), th('Party GSM'), td(esc(r.Remark_Party_GSM))])}
    </tbody></table>

    ${shS('Printing')}
    <table style="${tblS}">${colG}<tbody>
      ${row([th('Design No.'), td(each((p) => p.design, r.Product_Artwork ?? r.PO_Artwork)), th('No. of Plates'), td(each((p) => p.plate, r.PlateNumber))])}
      ${row([th('Shade Card No.'), td(each((p) => p.shade, r.ShadeCardNo)), th('Art / Mat No.'), td(each((p) => p.artMat, r.Product_AW_Material_No))])}
      ${row([th('Machine Front'), td(esc(r.MachineFront_Name ?? r.MachineFront)), th('Color'), td(esc(r.MachineFront_Color))])}
      ${row([th('Machine Front 2'), td(esc(r.MachineFront2_Name ?? r.MachineFront2)), th('Color'), td(esc(r.MachineFront2_Color))])}
      ${row([th('Machine Back'), td(esc(r.MachineBack_Name ?? r.MachineBack)), th('Color'), td(esc(r.MachineBack_Color))])}
      ${row([th('BP No.'), td(each((p) => p.bp, r.BP_No)), th('Metallic Carton'), td(yn(r.MetallicCarton))])}
    </tbody></table>

    ${shS('Top-Finish (Coating &amp; Lamination)')}
    <table style="${tblS}">${colG}<tbody>
      ${row([th('Top Finish'), td(esc(r.Lamination_Type_Name ?? r.Lamination_Type)), th('Pre/Post Lam.'), td(esc(r.PrePostLamination))])}
      ${row([th('Foiling Color'), td(esc(r.FoilingColor)), th('Film Size'), td(esc(r.FilmSize_Micron))])}
      ${row([th('Spot UV'), td(yn(r.SpotUV)), th('Screen UV'), td(yn(r.ScreenUV))])}
      ${row([th('Online Coating'), td(yn(r.OnlineCoating)), th('Offline Coating'), td(yn(r.OfflineCoating))])}
      ${/* BaseCoat: blank cell for products that predate the flag
           (BaseCoat is NULL) - shown as Yes/No only when the product
           master has explicitly saved a value.  Old job cards stay
           empty; new products carry a real Yes/No. */
        row([th('Base Coat'), td(r.BaseCoat == null ? '' : yn(r.BaseCoat)), th(''), td('')])}
    </tbody></table>

    ${shS('Die &amp; Carton Structure')}
    <table style="${tblS}">${colG}<tbody>
      ${row([th('Die No.'), td(esc(r.Die_Cutting)), th('Ups'), td(esc(r.Ups))])}
      ${row([th('Carton Type'), td(esc(r.CartonType)), th('3-Ply Carton'), td(yn(r.ThreePlyCarton))])}
      ${row([th('Product L×W×H'), td(each((p) => p.size, [r.Product_Length, r.Product_Width, r.Product_Height].filter(Boolean).join(' × '))), th('Braille'), td(yn(r.Braille))])}
      ${row([th('Online Embossing'), td(yn(r.OnlineEmbossing)), th('Offline Embossing'), td(yn(r.OfflineEmbossing))])}
      ${row([th('Embossing No.'), td(esc(r.EmbossingNo)), th(''), td('')])}
    </tbody></table>

    ${remarksHtml}

    ${r.ReissueReason ? `
    <div style="margin-top:8px;padding:6px 10px;border:2px solid #b91c1c;background:#fef2f2;">
      <span style="font-weight:900;font-size:11px;text-transform:uppercase;color:#b91c1c;">Reissued: </span>
      <span style="font-weight:600;font-size:11px;color:#1a1a1a;">${esc(r.ReissueReason)}</span>
    </div>` : ''}

    <div style="display:flex;justify-content:space-between;gap:40px;margin-top:30px;margin-bottom:6px;">
      <div style="flex:1;text-align:center;border-top:1px solid #000;padding-top:4px;font-size:10px;font-weight:700;">Created By (Signature)</div>
      <div style="flex:1;text-align:center;border-top:1px solid #000;padding-top:4px;font-size:10px;font-weight:700;">Checked By (Signature)</div>
    </div>

    <div style="font-size:8px;font-weight:600;text-align:right;margin-top:8px;color:#333;">
      All dimensions in mm (inch equivalents in brackets). Generated by Ab CRM v2 on ${new Date().toLocaleString()}.
    </div>
  `;
}

export function JobCardHistoryPrintPage() {
  const { historyId } = useParams();
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const q = useQuery({
    queryKey: ['job-history-print', historyId],
    queryFn: () => api<PrintRow & { Changed_On: string | null; Changed_By_Name: string | null; Change_Reason: string | null }>(
      `/api/job-orders/history/${historyId}/print`
    ),
  });

  async function downloadPdf() {
    const el = printRef.current;
    if (!el || downloading) return;
    setDownloading(true);
    try {
      const jobId = q.data?.Job_ID ?? historyId;
      const filename = `Job-Card-History-${String(jobId).replace(/\//g, '-')}.pdf`;
      await html2pdf().set({
        margin: 0,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      } as any).from(el).save();
    } finally {
      setDownloading(false);
    }
  }

  if (q.isLoading) return <div className="p-6 text-slate-500">Loading historical job card...</div>;
  if (q.isError || !q.data) {
    return (
      <div className="p-6">
        <p className="text-red-600">Could not load historical card: {(q.error as Error)?.message ?? 'unknown'}</p>
        <Link to={listReturnUrl('/job-orders')} className="text-brand underline text-sm">Back to Job Orders</Link>
      </div>
    );
  }
  const r = q.data;
  const sheetSizeRaw = r.Resolved_Sheet_Size ?? r.Sheet_Size ?? '';
  const sheetGsm = r.Sheet_GSM_From_Sheet
    || (/\((\d+(?:\.\d+)?)\)\s*$/.exec(sheetSizeRaw)?.[1] ?? '')
    || r.Remark_Party_GSM
    || '';
  const orderType = (r.Job_OrderType ?? r.PO_OrderType ?? '').trim();
  // The mm figures print whole; the inches are worked out from the exact mm
  // (488.95 mm is 19.25 in — from the shortened 488 it came out as 19.21).
  const sheetSizeExact = sheetSizeRaw.replace(/\s*\(\d+(?:\.\d+)?\)\s*$/, '');
  const sheetCutExact = [r.Sheet_CutSize_length, r.Sheet_CutSize_Width].filter(Boolean).join(' x ');
  // Whole mm with the inches beside; a legacy card's inches as they were entered.
  const { display: sheetSizeDisplay, suffix: sheetSizeIn } = sheetDimsForCard(sheetSizeExact);
  const { display: sheetCutDisplay, suffix: sheetCutIn } = sheetDimsForCard(sheetCutExact);
  const poQty = cardPoQty(r, []);
  const sheets = splitSheets(r.Client_Qty, r.Ups, r.TotalSheet_withWastage, r.Total_Boxes, poQty);
  const cardHtml = buildCardHtml(r, { sheetSizeDisplay, sheetCutDisplay, sheetSizeIn, sheetCutIn, sheetGsm, orderType, sheets, linkedPos: [], poQty });

  const changedOn = r.Changed_On ? new Date(r.Changed_On).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          .no-print { display: none !important; }
          body { margin: 0 !important; padding: 0 !important; }
          @page { margin: 0; size: A4; }
        }
      `}} />

      <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Arial, sans-serif' }}>
        <Link to={listReturnUrl('/job-orders')} style={{ fontSize: '13px', color: '#475569', textDecoration: 'none' }}>Back to Job Orders</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '12px', color: '#b91c1c', fontWeight: 700, border: '1px solid #fca5a5', borderRadius: '4px', padding: '4px 10px', background: '#fef2f2' }}>
            HISTORICAL VERSION — {changedOn} by {r.Changed_By_Name || 'System'}
          </span>
          <button onClick={() => window.print()}
            style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Print
          </button>
          <button onClick={downloadPdf} disabled={downloading}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#014868', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.7 : 1 }}>
            {downloading ? 'Generating…' : 'Save PDF'}
          </button>
        </div>
      </div>

      <div
        ref={printRef}
        style={{ width: '210mm', margin: '0 auto', padding: '12mm 15mm', boxSizing: 'border-box', background: '#fff', color: '#000', lineHeight: '1.4', fontFamily: 'Arial, Helvetica, sans-serif' }}
        dangerouslySetInnerHTML={{ __html: cardHtml }}
      />
    </>
  );
}
